import { useMemo } from 'react';
import { subscribe } from 'valtio';
import * as z from 'zod';
import { gql } from '@soundxyz/gql-string';
import { assertGetNonNullable } from '@soundxyz/utils/assert';
import { useAuth } from '@context/auth/base';
import { authUserPersistenceStore } from '@context/auth/persistence';
import { resetOperations, SpotifyConnectStateDocument, useQuery } from '@graphql/reactQuery';
import { PersistenceStorage } from '@store/persistence';
import { useIsHydrated } from './useIsHydrated';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation SpotifyConnect($input: MutationConnectSpotifyInput!) {
    connectSpotify(input: $input) {
      __typename
      ... on MutationConnectSpotifySuccess {
        data {
          id
        }
      }
      ... on Error {
        message
      }
    }
  }

  query SpotifyConnectState {
    spotifyConnection {
      id
    }
  }
`);

const SPOTIFY_CLIENT_ID = assertGetNonNullable(
  process.env.SPOTIFY_CLIENT_ID,
  'Missing SPOTIFY_CLIENT_ID',
);

export const SpotifyConnectState = PersistenceStorage({
  schema: z.object({
    state: z.string(),

    userId: z.string().uuid(),

    overrideAccount: z.boolean().default(false),

    justConnected: z.boolean().default(false),
  }),
  key: 'spotify-connect-state',
  eager: true,
});

/**
 * We want to reset this "state" random value when the user changes
 */
function resetSpotifyConnectState() {
  if (!SpotifyConnectState.state.value) return;

  const userId = authUserPersistenceStore.state.value?.data.authUser?.id;

  if (!userId) return;

  if (SpotifyConnectState.state.value.userId !== userId) {
    newSpotifyConnectState({ userId, overrideAccount: false, justConnected: false });
  }
}

subscribe(authUserPersistenceStore.state, resetSpotifyConnectState);
subscribe(SpotifyConnectState.state, resetSpotifyConnectState);

function getRandomSpotifyState(length = 16) {
  return Math.round(Math.pow(36, length + 1) - Math.random() * Math.pow(36, length))
    .toString(36)
    .slice(1);
}

export function newSpotifyConnectState({
  userId,
  overrideAccount,
  justConnected,
}: {
  userId: string;
  overrideAccount: boolean;
  justConnected: boolean;
}) {
  return SpotifyConnectState.set({
    state: getRandomSpotifyState(),

    userId,

    overrideAccount,

    justConnected,
  });
}

authUserPersistenceStore.initialValue.then(userValue => {
  if (!userValue?.data.authUser) return;

  const userId = userValue.data.authUser.id;

  SpotifyConnectState.initialValue.then(connectValue => {
    if (connectValue) return;
    newSpotifyConnectState({ userId, overrideAccount: false, justConnected: false });
  });
});

SpotifyConnectState.onTabSync(() => {
  resetOperations({
    operations: [SpotifyConnectStateDocument],
  });
});

export function useSpotifyAuth() {
  const isHydrated = useIsHydrated();

  const { isLoading, value } = SpotifyConnectState.useStore();

  const spotifyConnectState = value?.state;

  const { isAuthenticatedWithAPI, authUser } = useAuth();

  const authUserId = authUser?.id;

  const { data: isSpotifyAlreadyConnected = false, isInitialLoading } = useQuery(
    SpotifyConnectStateDocument,
    {
      staleTime: 0,
      enabled: isAuthenticatedWithAPI,
      filterQueryKey: {
        authUserId,
      },
      select(data) {
        return data.data.spotifyConnection != null;
      },
    },
  );

  return useMemo(() => {
    if (!isHydrated || isLoading || isInitialLoading) {
      return {
        type: 'loading',
      } as const;
    }

    if (isSpotifyAlreadyConnected) {
      return {
        type: 'already-connected',
      } as const;
    }

    if (!spotifyConnectState || !isAuthenticatedWithAPI || !authUserId) {
      return {
        type: 'not-authenticated',
      } as const;
    }
    return {
      type: 'spotify-auth',
      link: spotifyAuthorizationLink({ state: spotifyConnectState }),
      authUserId,
    } as const;
  }, [
    isHydrated,
    isLoading,
    isInitialLoading,
    isSpotifyAlreadyConnected,
    spotifyConnectState,
    isAuthenticatedWithAPI,
    authUserId,
  ]);
}

export function spotifyAuthorizationLink({ state }: { state: string }) {
  /**
   * Reference:
   *
   * https://developer.spotify.com/documentation/web-api/tutorials/code-flow
   */

  const url = new URL('https://accounts.spotify.com/authorize');

  url.searchParams.append('client_id', SPOTIFY_CLIENT_ID);
  url.searchParams.append('response_type', 'code');
  url.searchParams.append('redirect_uri', new URL('/spotify-callback', window.location.href).href);
  url.searchParams.append('state', state);
  url.searchParams.append('scope', 'user-library-read user-library-modify');

  return url.href;
}
